import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { selectMerchantId } from 'src/app/core/client-configuration/client-configuration.selectors';
import { selectOrderMerchantId } from 'src/app/core/merchant-details/order-merchant-details/order-merchant-details.selectors';
import { showToastAction } from 'src/app/core/toast/toast.actions';
import { ToastType } from 'src/app/core/toast/toast.state';
import { emailReceipt } from 'src/app/core/user/user.actions';
import { updateCartItemsRequested } from 'src/app/feature/cart/redux/cart.actions';
import { selectCartId, selectCartKey } from 'src/app/feature/cart/redux/cart.selectors';
import { APPCONFIG_BOOKINGS } from 'src/app/shared/enums/application-config.enums';
import { apiResponse, postApiRequest } from '../../../core/api/api.actions';
import { responseRequestType } from '../../../core/api/api.utilities';
import { LocaleService } from '../../../core/locale/locale.service';
import { showNotificationAction } from '../../../core/notification/notification.actions';
import { NotificationType } from '../../../core/notification/notification.state';
import { hideModifyPageSpinner, hidePageSpinner } from '../../../core/page-spinner/page-spinner.actions';
import { selectJwt } from '../../../core/user/user.selectors';
import { dayjs } from '../../../dayjs.lib';
import { ApiRequestType } from '../../../shared/enums/api.enums';
import {
	selectRecoverEmail,
	selectRecoverEToken,
	selectRecoverOrderId,
	selectRecoverOToken,
} from '../../recover/recover.selectors';
import { EventCalendar } from '../models/event-calendar.model';
import { addEditBookingFailed, eventDataReceived } from './bookings.actions';

@Injectable({
	providedIn: 'root',
})
export class BookingsEffects {
	/**
	 * Handles GetEventDatesByCalendar response
	 * If successful stores the results in state for use in the bookings modify component
	 */
	getEventDatesByCalendarResponse$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(
					(resp) =>
						resp.requestType === ApiRequestType.GET_ENTITLEMENT_KEYWORD_CALENDAR ||
						resp.requestType === ApiRequestType.GET_EVENT_DATES_BY_CALENDAR
				),
				withLatestFrom(this._store$.select(selectAppConfig([APPCONFIG_BOOKINGS]))),
				map(([action, config]) => {
					if (action.isOk) {
						if (action.customData?.disableLoginCall) {
							return;
						}

						let eventDates: EventCalendar;

						if (action.requestType === ApiRequestType.GET_ENTITLEMENT_KEYWORD_CALENDAR) {
							eventDates = {
								event_entitlement_id: action.params?.event_entitlement_id,
								year: +dayjs(action.params?.start_date).format('M'),
								month: +dayjs(action.params?.start_date).format('YYYY'),
								dates: action.response.DATES?.[0]?.D?.map((date) => {
									const isDateOnly = !date.T && !date.PS?.[0]?.P?.[0]?.T;
									return {
										id: date.PS?.[0].P?.[0]?.id,
										newCustomerType: date.PS?.[0]?.P?.[0]?.CT?.[0]?.id,
										date: dayjs(date.date),
										dynamicPricingId: date.PS?.[0]?.P?.[0]?.CT?.[0]?.dynamic_pricing_id,
										isDateOnly,
										totalFeeAmountDue: date.total_fee_amount_due,
										totalRetailAmountDue: isDateOnly ? date.total_retail_amount_due : 0,
										totalAmountDue: isDateOnly ? date.total_amount_due : 0,
										totalTaxAmountDue: date.total_tax_amount_due,
										displayUpgradeAmount: isDateOnly
											? config.bookings.booking_tax_inclusive_pricing
												? date.total_amount_due
												: date.total_retail_amount_due
											: 0,
										times:
											date.T?.map((time) => {
												return {
													available: +time.available,
													capacity: +time.capacity,
													dynamicPricingId: time.CT?.[0]?.dynamic_pricing_id,
													time: time.time,
													used: +time.used,
													newCustomerType: time.CT?.[0]?.id,
													totalFeeAmountDue: time.total_fee_amount_due,
													totalRetailAmountDue: time.total_retail_amount_due,
													totalAmountDue: time.total_amount_due,
													totalTaxAmountDue: time.total_tax_amount_due,
													displayUpgradeAmount: config.bookings.booking_tax_inclusive_pricing
														? time.total_amount_due
														: time.total_retail_amount_due,
												};
											}) ??
											date.PS?.[0].P?.[0]?.T?.map((time) => {
												return {
													available: +time.available,
													capacity: +time.capacity,
													dynamicPricingId: time.CT?.[0]?.dynamic_pricing_id,
													time: time.time,
													used: +time.used,
													newCustomerType: time.CT?.[0]?.id,
													totalFeeAmountDue: time.total_fee_amount_due,
													totalRetailAmountDue: time.total_retail_amount_due,
													totalAmountDue: time.total_amount_due,
													totalTaxAmountDue: time.total_tax_amount_due,
													displayUpgradeAmount: config.bookings.booking_tax_inclusive_pricing
														? time.total_amount_due
														: time.total_retail_amount_due,
												};
											}),
									};
								}),
							};
						} else {
							eventDates = {
								event_calendar_id: action.response.event_calendar_id,
								event_id: action.response.event_id,
								year: +action.response.year,
								month: +action.response.month,
								dates: action.response.D.map((date) => ({
									date: dayjs(date.date),
									isDateOnly: !date.T,
									times: date.T?.map((time) => ({
										available: +time.available,
										capacity: +time.capacity,
										time: time.time,
										used: +time.used,
									})),
								})),
							};
						}
						this._store$.dispatch(hideModifyPageSpinner({ initiator: 'Event Dates Received' }));
						this._store$.dispatch(eventDataReceived({ event: eventDates }));
					} else {
						this._store$.dispatch(hidePageSpinner({ initiator: 'GetEventDatesByCalendar request failed' }));
						this._store$.dispatch(
							showNotificationAction({
								buttonLabel: this._locale.get('common.close'),
								dialogType: NotificationType.GENERAL,
								initiator: `GetEventDatesByCalendar: Failed`,
								message: this._locale.get(`errorcode.${action.response.error_code}`),
							})
						);
					}
				})
			),
		{ dispatch: false }
	);

	addEditBookingResponse$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(apiResponse),
				filter(responseRequestType(ApiRequestType.ADD_EDIT_BOOKING)),
				withLatestFrom(
					this._store$.pipe(select(selectRecoverEmail)),
					this._store$.pipe(select(selectRecoverOrderId)),
					this._store$.pipe(select(selectRecoverEToken)),
					this._store$.pipe(select(selectRecoverOToken)),
					this._store$.pipe(select(selectJwt)),
					this._store$.pipe(select(selectCartId)),
					this._store$.pipe(select(selectCartKey)),
					this._store$.pipe(select(selectMerchantId)),
					this._store$.pipe(select(selectOrderMerchantId)),
					this._store$.pipe(select(selectAppConfig([APPCONFIG_BOOKINGS])))
				),
				tap(
					([
						action,
						email,
						orderId,
						eToken,
						oToken,
						jwt,
						cartId,
						cartKey,
						merchantId,
						orderMerchantId,
						configs,
					]) => {
						if (action.isOk) {
							if (!action?.response?.error_code) {
								this._store$.dispatch(
									showToastAction({
										message: this._locale.get('booking.addeditbookingsuccess'),
										toastType: ToastType.SUCCESS,
									})
								);
							}
							const params = {
								eToken,
								oToken,
								jwt,
								email,
								order_id: orderId,
							};
							this._store$.dispatch(
								postApiRequest({
									requestType: ApiRequestType.BOOKING_PORTAL_LOGIN,
									params,
								})
							);

							if (action?.params?.PAYMENT || configs.bookings?.email_receipt_all_booking_modifications) {
								this._store$.dispatch(
									emailReceipt({
										merchantId: orderMerchantId ?? merchantId,
										orderId,
										email,
									})
								);
							}
						} else {
							this._store$.dispatch(hidePageSpinner({ initiator: 'AddEditBooking request failed' }));
							this._store$.dispatch(addEditBookingFailed());
							// If call initiated from Upgrade flow
							// remove items from cart
							if (cartId && cartKey) {
								this._store$.dispatch(
									updateCartItemsRequested({
										cart_id: cartId,
										cart_key: cartKey,
										merchant_id: merchantId,
										ITEMS: [
											{
												ITEM: [],
											},
										],
									})
								);
							}
							this._store$.dispatch(
								showNotificationAction({
									buttonLabel: this._locale.get('common.close'),
									dialogType: NotificationType.GENERAL,
									initiator: `AddEditBooking: Failed`,
									message: this._locale.get(`errorcode.${action.response.error_code}`),
								})
							);
						}
					}
				)
			),
		{ dispatch: false }
	);
	/**
	 * Our effects constructor
	 * @param _actions$ The action stream
	 * @param _store$ the store
	 * @param _locale the locale service
	 */
	constructor(private _actions$: Actions, private _store$: Store, private _locale: LocaleService) {}
}
